import { useState } from 'react'
import { AlertCircle, ExternalLink, FolderOpen, Trash2 } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useGetDocumentUrl, useDeleteDocument } from '../../hooks/backend/documents'
import { writeErrorMessage } from '../../utils/writeError'
import { UploadControl, type UploadCategory } from './UploadControl'
import {
  type DocumentRow,
  fileIcon,
  formatFileSize,
  groupByCategory,
  relativeTime,
} from '../../utils/documents'

// Renders the upload control, the grouped document list (with per-row open +
// delete), and the footer. Fetching is owned by the wrapping dialogs — they pass
// scope, capabilities, and an onChanged callback to re-fetch after a mutation.
export function DocumentsBody({
  rows,
  loading,
  error,
  emptyText,
  syncedAt,
  syncStale = null,
  categories,
  uploadScope,
  canUpload,
  canDelete,
  attachesTo,
  onChanged,
}: {
  rows: DocumentRow[]
  loading: boolean
  error: string | null
  emptyText: string
  syncedAt: string | null
  // From v_document_sync_footer. True when the Drive sync has not completed a
  // good run in three hours; null when it could not be read.
  syncStale?: boolean | null
  categories: UploadCategory[]
  uploadScope: { vehicle_id?: string; owner_person_id?: string }
  canUpload: boolean
  canDelete: boolean
  attachesTo: 'vehicle' | 'owner'
  onChanged: () => void
}) {
  const groups = groupByCategory(rows)

  return (
    // min-w-0: this is a grid item inside DialogContent, and a grid item's
    // minimum width defaults to its content, so one long unbroken file name
    // widened the whole dialog past its edge instead of truncating.
    <div className="flex min-w-0 flex-col">
      {canUpload && (
        <UploadControl categories={categories} scope={uploadScope} onUploaded={onChanged} />
      )}

      <div className="max-h-[55vh] overflow-y-auto">
        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading documents…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <FolderOpen className="h-6 w-6" />
            {emptyText}
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <section key={group.code}>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                  <span className="ml-1.5 tabular-nums font-normal opacity-70">
                    {group.items.length}
                  </span>
                </h3>
                <ul className="overflow-hidden rounded-md border border-border">
                  {group.items.map((doc, i) => (
                    <li
                      key={doc.id}
                      className={i > 0 ? 'border-t border-border' : undefined}
                    >
                      <DocumentItem
                        doc={doc}
                        canDelete={canDelete}
                        attachesTo={attachesTo}
                        onChanged={onChanged}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Footer: provenance + last synced */}
      <div className="mt-4 space-y-1 border-t border-border pt-3">
        {syncStale ? (
          // Said plainly, because a list that looks complete and is not is the
          // failure this footer exists to prevent.
          <p className="flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Google Drive sync is stale: the last good sync was {relativeTime(syncedAt)}.
              Files added in Drive since then are not on this list yet. Tell the platform team.
            </span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Synced from Google Drive · last synced {relativeTime(syncedAt)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground/80">
          Files upload to both the platform and Google Drive.
        </p>
      </div>
    </div>
  )
}

function DocumentItem({
  doc,
  canDelete,
  attachesTo,
  onChanged,
}: {
  doc: DocumentRow
  canDelete: boolean
  attachesTo: 'vehicle' | 'owner'
  onChanged: () => void
}) {
  const Icon = fileIcon(doc.mime_type)
  const size = formatFileSize(doc.size_bytes)
  const title = doc.title || 'Untitled document'

  const getUrl = useGetDocumentUrl()
  const del = useDeleteDocument()
  const [deleting, setDeleting] = useState(false)

  const storagePath = doc.storage_path
  const sourceTable = doc.source_table ?? 'vehicle_documents'
  // Onboarding and driver papers live in their own buckets and are opened by
  // id; the handler finds the file. Only vehicle documents can be deleted here,
  // because the delete path removes a vehicle_documents row and its Drive copy.
  const isVehicleStore = sourceTable === 'vehicle_documents'
  const openable = storagePath != null || doc.drive_url != null || !isVehicleStore

  async function handleOpen() {
    if (getUrl.loading || !openable) return
    try {
      const res = await getUrl.trigger({
        id: doc.id,
        source_table: sourceTable,
        storage_path: storagePath,
        drive_url: doc.drive_url,
      }).result
      const url = (res as { url: string | null } | undefined)?.url ?? null
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('This document has no viewable file.')
      }
    } catch (err) {
      toast.error('Could not open document', { description: writeErrorMessage(err) })
    }
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation()
    if (deleting) return
    if (!window.confirm(`Delete "${title}"? This removes it from the platform and Google Drive.`)) {
      return
    }
    setDeleting(true)
    try {
      await del.trigger({
        document_id: doc.id,
        attaches_to: doc.attaches_to || attachesTo,
      }).result
      toast.success('Document deleted')
      onChanged()
    } catch (err) {
      toast.error('Could not delete document', { description: writeErrorMessage(err) })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex items-center gap-2.5 px-3 py-2 text-sm">
      <button
        type="button"
        onClick={handleOpen}
        disabled={!openable || getUrl.loading}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left transition-colors hover:text-[hsl(var(--brand))] disabled:cursor-default"
      >
        <Icon className="h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
        <span className="min-w-0 flex-1 truncate text-foreground">{title}</span>
        {doc.vehicle_registration && (
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {doc.vehicle_registration}
          </span>
        )}
        {doc.status_label && (
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {doc.status_label}
          </span>
        )}
        {size && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{size}</span>
        )}
        {getUrl.loading ? (
          <Spinner className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>

      {canDelete && isVehicleStore && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          aria-label={`Delete ${title}`}
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          {deleting ? <Spinner className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  )
}
